import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from "react";
import { reportDiagnostic, track } from "../telemetry/telemetry";

/**
 * WHAT A TESTER SEES WHEN SOMETHING GOES WRONG, AND WHEN THEY FIRST ARRIVE
 * (Phase 13C, spec §59D/§59E).
 *
 * Two small pieces of chrome that only exist in the hosted beta:
 *
 *   • an error boundary that shows a sentence instead of a stack trace, and
 *     a REFERENCE the tester can quote — the sanitized record is already
 *     with the administrator by the time they read it;
 *   • a short welcome for a brand-new account, which is a signpost, not a
 *     tutorial, and never appears again once it is dismissed.
 */

export class BetaErrorBoundary extends Component<
  { children: ReactNode; onReset?: () => void },
  { failed: boolean; reference: string }
> {
  state = { failed: false, reference: "" };

  static getDerivedStateFromError(): { failed: boolean; reference: string } {
    return { failed: true, reference: "" };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    void reportDiagnostic({
      category: "render",
      message: error.message || "The screen failed to render.",
      operation: "react.render",
      detail: (info.componentStack ?? "").slice(0, 600),
    }).then((reference) => {
      if (reference) this.setState((previous) => ({ ...previous, reference }));
    });
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="panel">
        <div className="error-panel" role="alert">
          <h2>Something went wrong on this screen</h2>
          <p>
            Your work has not been lost — Berrier Reno saves as you go. Go back and try again, and if it keeps
            happening, send feedback and quote the reference below.
          </p>
          {this.state.reference && (
            <p className="cell-strong">
              Reference: <code>{this.state.reference}</code>
            </p>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            this.setState({ failed: false, reference: "" });
            this.props.onReset?.();
          }}
        >
          Back to my projects
        </button>
      </div>
    );
  }
}

const WELCOME_KEY = "berrier.welcome-seen";

/**
 * Shown once, to an account that has not created anything yet. Deliberately
 * three sentences and one button: the point is to get them into a project,
 * not to explain the product.
 */
export function BetaWelcome({ email, onStart }: { email: string; onStart: () => void }) {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(`${WELCOME_KEY}:${email}`) === "1");
    } catch {
      setDismissed(false);
    }
  }, [email]);

  const close = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(`${WELCOME_KEY}:${email}`, "1");
    } catch {
      /* a browser with storage disabled simply sees it again */
    }
  };

  if (dismissed) return null;

  return (
    <section className="panel welcome-panel">
      <div className="panel-header">
        <h2>Welcome to Berrier Reno — Early Access</h2>
        <button className="btn btn-small" onClick={close}>
          Dismiss
        </button>
      </div>
      <p className="workspace-note">
        Berrier Reno helps you design, estimate, source, track and analyze renovation projects — from what a house
        costs to buy through what it earned when it sold.
      </p>
      <p className="muted workspace-note">
        Everything starts with a project. Yours are private to this account: nobody else can see them unless you share a project.
      </p>
      <button
        className="btn btn-primary"
        onClick={() => {
          track("area.viewed", undefined, "welcome");
          close();
          onStart();
        }}
      >
        Create your first project
      </button>
      <p className="muted proc-subline">
        You are using Early Access, so features and wording may change. Use <strong>Send feedback</strong> at the top
        of the window whenever something is wrong or confusing.
      </p>
    </section>
  );
}
